import React, { FunctionComponent } from "react";
import Link from "next/link";

import { truncateText } from "src/lib/functions";
import { Project, SearchProjectsQuery } from "src/lib/graphql/types";
import removeMarkdown from "src/lib/removeMarkdown";

import ProjectAvatar from "../ProjectAvatar";
import ForkIcon from "../../icons/ForkIcon";
import LockIcon from "../../icons/LockIcon";
import { useUser } from "src/hooks/useUser";
import DependentsIcon from "../../icons/DependentsIcon";
import StarBadge from "../StarBadge";

type SearchResultType = SearchProjectsQuery["searchProjects"]["results"][0];

type SearchResultCardProps = {
  result: SearchResultType;
};

const SearchResultCard: FunctionComponent<SearchResultCardProps> = ({
  result,
}) => {
  const [user] = useUser();
  const mine = user && user.shortname === result.namespace;

  let avatar, dependents, link, forkCount, starCount, type;
  if ("type" in result) {
    ({ avatar, dependents, forkCount, starCount, type } = result);
    link = `/${result.pathWithNamespace}`;
  } else {
    // it's a Subject
    link = `/@${result.namespace}/schemas/${result.name}`;
    type = "Subject";
  }

  return (
    <Link href={link} prefetch={false}>
      <a className="search-result-link">
        <div className={`search-result-card ${type.toLowerCase()}`}>
          {type === "Simulation" && avatar && (
            <div className="search-result-avatar">
              <ProjectAvatar project={result} size={"100%"} />
            </div>
          )}
          <div className="search-result-card-content">
            <div className="search-result-metadata">
              <span className="search-result-shortname">
                {result.pathWithNamespace}
              </span>
              <span className="type">{type}</span>
              {!!forkCount && (
                <div className="fork-count">
                  <ForkIcon />
                  {forkCount}
                </div>
              )}
              {!!dependents && (
                <div className="dependents-count">
                  <DependentsIcon />
                  {dependents}
                </div>
              )}
              {result.visibility === "private" && (
                <span className="private-indicator">
                  <LockIcon />
                  Only visible to {mine ? "you" : `@${result.namespace}`}
                </span>
              )}
              {!!starCount && (
                <StarBadge
                  project={
                    result as Pick<Project, "pathWithNamespace" | "starCount">
                  }
                />
              )}
            </div>
            <div className="search-result-title">
              <span className="title">{result.name}</span>
            </div>
            <div className="search-result-desc small-text">
              {truncateText(removeMarkdown(result.description), 200)}
            </div>
          </div>
        </div>
      </a>
    </Link>
  );
};

export default SearchResultCard;
